<script lang="ts">
  /**
   * STANDARDIZED BUTTON — Use this instead of raw <button> elements.
   * Ensures consistent styling, loading states, and accessibility.
   *
   * Usage:
   * <Button variant="primary" loading={isSaving} onclick={handleSave}>Save</Button>
   */
  import type { Snippet } from 'svelte';

  interface Props {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    loading?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    onclick?: () => void;
    children: Snippet;
  }

  let {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    type = 'button',
    onclick,
    children,
  }: Props = $props();

  const baseClasses = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-900';

  const variantClasses: Record<string, string> = {
    primary: 'bg-brand-600 text-white hover:bg-brand-500 focus:ring-brand-500',
    secondary: 'bg-surface-700 text-surface-200 hover:bg-surface-600 focus:ring-surface-500',
    danger: 'bg-red-600/20 text-red-400 hover:bg-red-600/30 focus:ring-red-500',
    ghost: 'bg-transparent text-surface-400 hover:text-surface-200 hover:bg-surface-800 focus:ring-surface-500',
  };

  const sizeClasses: Record<string, string> = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
</script>

<button
  {type}
  {onclick}
  disabled={disabled || loading}
  class="{baseClasses} {variantClasses[variant]} {sizeClasses[size]}"
>
  {#if loading}
    <div class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
  {/if}
  {@render children()}
</button>
